
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import { PageContainer } from '@/components/layout/PageContainer';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Calendar, MapPin, Phone, Plus, Search } from 'lucide-react';

async function fetchVenues(): Promise<Tables<'user_venues'>[]> {
  const { data, error } = await supabase
    .from('user_venues')
    .select('*')
    .order('name', { ascending: true });
  if (error) throw error;
  return data || [];
}

const VenuesPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const { data: venues = [], isLoading, error } = useQuery({
    queryKey: ['venues'],
    queryFn: fetchVenues,
  });

  // Filter venues based on search query
  const filteredVenues = venues.filter(venue => 
    venue.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (venue.address && venue.address.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (isLoading) {
    return (
      <PageContainer title="Venues" subtitle="Manage your venues">
        <div className="text-center text-muted-foreground">Loading venues...</div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer title="Venues" subtitle="Manage your venues">
        <div className="text-center text-destructive">Error loading venues.</div>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="Venues" subtitle="Manage your venues">
      <div className="space-y-4">
        {/* Search and actions bar */}
        <div className="flex items-center space-x-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              id="venue-search"
              placeholder="Search venues..." 
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button size="icon" className="flex-shrink-0 animate-pulse-subtle">
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Venues list */}
        <div className="space-y-3 pb-20">
          {filteredVenues.length > 0 ? (
            filteredVenues.map((venue, index) => (
              <Card 
                key={venue.id}
                className="p-4 hover-lift touch-feedback active:scale-98 animate-slide-in-bottom"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center">
                      <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary mr-3">
                        <MapPin className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-medium">{venue.name}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">{venue.address}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center text-sm text-muted-foreground">
                    {/* Phone number can be added later if needed */}
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <div className="py-8 text-center">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
              <h3 className="text-lg font-medium">No venues found</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Try adjusting your search or add a new venue
              </p>
              <Button className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Add New Venue
              </Button>
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default VenuesPage;
